// ============================================================================
// CURRENT CLEANING TASK HOOK
// Fetches the current/today's task for a specific property and cleaner
// ============================================================================
// Note: We fetch reservations in a separate query instead of embedding in
// cleaning_tasks select to avoid 400 Bad Request (e.g. RLS/embed with cleaners).

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCleanerRecord } from "@/hooks/useCleaner";
import type { CurrentCleaningTask } from "@/types/cleaning";
import type { Json } from "@/integrations/supabase/types";
import { resolveJobLeaveOutStaging } from "@/lib/guestAmenityStaging";
import { canStartTurnoverClean, withTurnoverClock, type ReservationDateRow } from "@/lib/turnoverWindow";
import { getTaskIdsWithChecklistAssignmentRows, taskHasChecklistAssignmentRow } from "@/lib/cleaningTaskChecklistAssignments";
import { runWithStamp, useWithPendingStamps } from "@/lib/stampOutbox";
import { fetchArrivalTasksForJob } from "@/lib/arrivalChecklist";
import { stampColumns, type JobStamp } from "@/lib/locationStamps";

export type { CurrentCleaningTask };

const TASK_SELECT = "*, checklists(id, name, access_verification_settings, guest_amenities_instructions, guest_amenity_staging), properties(id, name, address)";

async function attachReservation(
  row: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const reservationId = row.reservation_id as string | null;
  if (!reservationId) {
    row.reservations = null;
    return row;
  }
  const { data: res } = await supabase
    .from("reservations")
    .select("guest_name, guest_count, check_in_time, check_out_time, check_out_previous, check_out_slipped_at, early_checkout_detected, checkout_unconfirmed_at")
    .eq("id", reservationId)
    .maybeSingle();
  row.reservations = res ?? null;
  return row;
}

/** When per-checklist rows exist, ignore a stale `cleaning_tasks.cleaner_id` unless this cleaner is also linked in those rows. */
async function allowMainCleanerRowForTask(taskId: string, viaAssignmentIds: string[]): Promise<boolean> {
  const j = await getTaskIdsWithChecklistAssignmentRows([taskId]);
  if (!j.has(taskId)) return true;
  return viaAssignmentIds.includes(taskId);
}

/** Enrich task row with assigned_checklists (for this cleaner) and include_inventory_check. */
async function enrichTaskForCleaner(
  raw: Record<string, unknown>,
  cleanerId: string
): Promise<CurrentCleaningTask> {
  const taskId = raw.id as string;
  const checklists = raw.checklists as {
    name: string;
    access_verification_settings?: unknown;
    guest_amenities_instructions?: string | null;
    guest_amenity_staging?: unknown;
  } | null;
  const properties = raw.properties as { name: string; address: string | null } | null;
  const reservations = raw.reservations as { guest_name: string | null; guest_count: number | null; check_in_time: string | null; check_out_time: string | null; early_checkout_detected?: boolean | null } | null;
  const base: CurrentCleaningTask = {
    id: taskId,
    property_id: raw.property_id as string,
    reservation_id: (raw.reservation_id as string | null) ?? null,
    status: raw.status as string,
    scheduled_date: raw.scheduled_date as string,
    checklist_completed: raw.checklist_completed as boolean | null,
    inventory_completed: raw.inventory_completed as boolean | null,
    checklist_id: raw.checklist_id as string | null,
    checklist_name: checklists?.name ?? null,
    checklist_access_verification_settings: checklists?.access_verification_settings ?? null,
    checklist_guest_amenities_instructions:
      (checklists?.guest_amenities_instructions && String(checklists.guest_amenities_instructions).trim()) || null,
    checklist_guest_amenity_staging: await resolveJobLeaveOutStaging(
      raw.property_id as string,
      checklists?.guest_amenity_staging,
    ),
    entry_walkthrough_completed: Boolean(raw.entry_walkthrough_completed),
    inventory_guest_phase_completed: Boolean(raw.inventory_guest_phase_completed),
    inventory_supply_phase_completed: Boolean(raw.inventory_supply_phase_completed),
    assigned_checklists: null,
    include_inventory_check: (raw.include_inventory_check as boolean) !== false,
    property_name: properties?.name ?? null,
    property_address: properties?.address ?? null,
    is_test: Boolean(raw.is_test),
    test_setup: raw.test_setup ?? null,
    started_at: raw.started_at as string | null,
    start_location_lat: (raw.start_location_lat as number | null) ?? null,
    start_location_override_reason: (raw.start_location_override_reason as string | null) ?? null,
    completed_at: raw.completed_at as string | null,
    duration_minutes: raw.duration_minutes as number | null,
    extra_dirty: raw.extra_dirty as boolean | null,
    extra_dirty_notes: raw.extra_dirty_notes as string | null,
    unit_empty_on_arrival: raw.unit_empty_on_arrival as boolean | null,
    early_cleaning_started: raw.early_cleaning_started as boolean | null,
    guest_name: reservations?.guest_name ?? null,
    guest_count: reservations?.guest_count ?? (typeof raw.guest_count === "number" ? raw.guest_count : null) ?? null,
    override_check_in_time: raw.override_check_in_time as string | null,
    override_check_out_time: raw.override_check_out_time as string | null,
    reservation_check_in_time: reservations?.check_in_time ?? null,
    reservation_check_out_time: reservations?.check_out_time ?? null,
    entry_walkthrough_state: (raw.entry_walkthrough_state as Json | null) ?? null,
    guest_setup_completed: Boolean(raw.guest_setup_completed),
    guest_setup_state: (raw.guest_setup_state as Json | null) ?? null,
  };
  const { data: assignmentRows } = await supabase
    .from("cleaning_task_checklist_assignments")
    .select("checklist_id")
    .eq("cleaning_task_id", taskId)
    .eq("cleaner_id", cleanerId);
  if (assignmentRows && assignmentRows.length > 0) {
    const names = await Promise.all(
      assignmentRows.map((r) => supabase.from("checklists").select("name").eq("id", r.checklist_id).maybeSingle())
    );
    base.assigned_checklists = assignmentRows.map((r, i) => ({
      checklist_id: r.checklist_id,
      checklist_name: (names[i]?.data as { name?: string } | null)?.name ?? "",
    }));
  }
  const propertyId = raw.property_id as string;
  const { data: clockRows } = await supabase
    .from("reservations")
        .select("id, start_date, end_date, guest_name, guest_count, check_in_time, check_out_time, check_out_previous, check_out_slipped_at, early_checkout_detected, checkout_unconfirmed_at, reservation_type")
    .eq("property_id", propertyId);
  return withTurnoverClock(base, (clockRows ?? []) as ReservationDateRow[]);
}

export const useCurrentCleaningTask = (propertyId: string | undefined, taskId?: string | null) => {
  const { data: cleaner } = useCleanerRecord();

  const query = useQuery({
    queryKey: ["current-cleaning-task", propertyId, cleaner?.id, taskId ?? ""],
    queryFn: async () => {
      if (!propertyId || !cleaner) return null;

      // When taskId is in URL, load by task id only (URL propertyId may be stale from resume/bookmark).
      if (taskId) {
        const { data: byIdTask, error: byIdError } = await supabase
          .from("cleaning_tasks")
          .select(TASK_SELECT)
          .eq("id", taskId)
          .maybeSingle();

        if (byIdError || !byIdTask) return null;

        const taskRow = await attachReservation(byIdTask as Record<string, unknown>);
        const hasJunctionRows = await taskHasChecklistAssignmentRow(taskId);
        const directAssign = taskRow.cleaner_id === cleaner.id && !hasJunctionRows;
        let viaAssignment = false;
        if (!directAssign) {
          const { data: assignRows } = await supabase
            .from("cleaning_task_checklist_assignments")
            .select("id")
            .eq("cleaning_task_id", taskId)
            .eq("cleaner_id", cleaner.id)
            .limit(1);
          viaAssignment = !!assignRows?.length;
        }
        if (!directAssign && !viaAssignment) return null;

        return enrichTaskForCleaner(taskRow, cleaner.id);
      }

      // Use local date (not UTC) for accurate "today" comparison
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      // First try to find an in_progress task: by cleaner_id on task or by assignment row
      const { data: assignmentTaskIds } = await supabase
        .from("cleaning_task_checklist_assignments")
        .select("cleaning_task_id")
        .eq("cleaner_id", cleaner.id);
      const viaAssignmentIds = (assignmentTaskIds ?? []).map((r) => r.cleaning_task_id);

      let inProgressTask: Record<string, unknown> | null = null;
      const { data: inProgressByCleaner, error: inProgressError } = await supabase
        .from("cleaning_tasks")
        .select(TASK_SELECT)
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleaner.id)
        .eq("status", "in_progress")
        .order("scheduled_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!inProgressError && inProgressByCleaner) {
        const row = await attachReservation(inProgressByCleaner as Record<string, unknown>);
        if (await allowMainCleanerRowForTask(row.id as string, viaAssignmentIds)) {
          inProgressTask = row;
        }
      }
      if (!inProgressTask && viaAssignmentIds.length > 0) {
        const { data: inProgressByAssign } = await supabase
          .from("cleaning_tasks")
          .select(TASK_SELECT)
          .eq("property_id", propertyId)
          .in("id", viaAssignmentIds)
          .eq("status", "in_progress")
          .order("scheduled_date", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (inProgressByAssign) inProgressTask = await attachReservation(inProgressByAssign as Record<string, unknown>);
      }

      if (inProgressError) {
        console.error("[useCurrentCleaningTask] Error fetching in_progress task:", inProgressError);
        return null;
      }

      const treatAsInProgress = inProgressTask && !(inProgressTask.checklist_completed && inProgressTask.inventory_completed);
      if (treatAsInProgress && inProgressTask) {
        return enrichTaskForCleaner(inProgressTask, cleaner.id);
      }

      // Then try to find a scheduled task for today or earlier (by cleaner_id or assignment)
      let scheduledTask: Record<string, unknown> | null = null;
      const { data: scheduledByCleaner, error: scheduledError } = await supabase
        .from("cleaning_tasks")
        .select(TASK_SELECT)
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleaner.id)
        .eq("status", "scheduled")
        .lte("scheduled_date", today)
        .order("scheduled_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!scheduledError && scheduledByCleaner) {
        const row = await attachReservation(scheduledByCleaner as Record<string, unknown>);
        if (await allowMainCleanerRowForTask(row.id as string, viaAssignmentIds)) {
          scheduledTask = row;
        }
      }
      if (!scheduledTask && viaAssignmentIds.length > 0) {
        const { data: scheduledByAssign } = await supabase
          .from("cleaning_tasks")
          .select(TASK_SELECT)
          .eq("property_id", propertyId)
          .in("id", viaAssignmentIds)
          .eq("status", "scheduled")
          .lte("scheduled_date", today)
          .order("scheduled_date", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (scheduledByAssign) scheduledTask = await attachReservation(scheduledByAssign as Record<string, unknown>);
      }
      if (scheduledError) {
        console.error("[useCurrentCleaningTask] Error fetching scheduled task:", scheduledError);
        return null;
      }
      if (scheduledTask) return enrichTaskForCleaner(scheduledTask, cleaner.id);

      // No task today or earlier: try next scheduled (future) task (by cleaner_id or assignment)
      let futureTask: Record<string, unknown> | null = null;
      const { data: futureByCleaner, error: futureError } = await supabase
        .from("cleaning_tasks")
        .select(TASK_SELECT)
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleaner.id)
        .eq("status", "scheduled")
        .gt("scheduled_date", today)
        .order("scheduled_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!futureError && futureByCleaner) {
        const row = await attachReservation(futureByCleaner as Record<string, unknown>);
        if (await allowMainCleanerRowForTask(row.id as string, viaAssignmentIds)) {
          futureTask = row;
        }
      }
      if (!futureTask && viaAssignmentIds.length > 0) {
        const { data: futureByAssign } = await supabase
          .from("cleaning_tasks")
          .select(TASK_SELECT)
          .eq("property_id", propertyId)
          .in("id", viaAssignmentIds)
          .eq("status", "scheduled")
          .gt("scheduled_date", today)
          .order("scheduled_date", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (futureByAssign) futureTask = await attachReservation(futureByAssign as Record<string, unknown>);
      }
      if (futureTask) return enrichTaskForCleaner(futureTask, cleaner.id);

      // If no pending task, check for a task completed today (by cleaner_id or assignment)
      let completedTask: Record<string, unknown> | null = null;
      const { data: completedByCleaner, error: completedError } = await supabase
        .from("cleaning_tasks")
        .select(TASK_SELECT)
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleaner.id)
        .eq("status", "completed")
        .gte("completed_at", `${today}T00:00:00`)
        .order("completed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!completedError && completedByCleaner) {
        const row = await attachReservation(completedByCleaner as Record<string, unknown>);
        if (await allowMainCleanerRowForTask(row.id as string, viaAssignmentIds)) {
          completedTask = row;
        }
      }
      if (!completedTask && viaAssignmentIds.length > 0) {
        const { data: completedByAssign } = await supabase
          .from("cleaning_tasks")
          .select(TASK_SELECT)
          .eq("property_id", propertyId)
          .in("id", viaAssignmentIds)
          .eq("status", "completed")
          .gte("completed_at", `${today}T00:00:00`)
          .order("completed_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (completedByAssign) completedTask = await attachReservation(completedByAssign as Record<string, unknown>);
      }
      if (completedError) {
        console.error("[useCurrentCleaningTask] Error fetching completed task:", completedError);
        return null;
      }
      if (completedTask) {
        return enrichTaskForCleaner(completedTask, cleaner.id);
      }

      return null;
    },
    enabled: !!propertyId && !!cleaner,
    staleTime: 0, // Always refetch when opening a property so "started" state is correct per task
  });
  // A start or finish still waiting to sync (no signal) shows as done.
  const data = useWithPendingStamps(query.data);
  return useMemo(() => ({ ...query, data }), [query, data]);
};

export interface StartLocationSnapshot {
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  distanceM: number | null;
  verified: boolean | null;
  overrideReason: string | null;
  capturedAt: string;
}

export const useStartCleaningTimer = () => {
  const queryClient = useQueryClient();
  const { data: cleaner } = useCleanerRecord();

  return useMutation({
    mutationFn: async ({
      taskId,
      propertyId,
      extraDirty,
      extraDirtyNotes,
      earlyCleaningStarted,
      startLocation,
      enforceScheduledDay,
      replayStamp,
    }: {
      taskId: string;
      propertyId: string;
      extraDirty?: boolean;
      extraDirtyNotes?: string | null;
      earlyCleaningStarted?: boolean;
      startLocation?: StartLocationSnapshot | null;
      /** When true (cleaner portal), first start is only allowed on the task's scheduled calendar day. */
      enforceScheduledDay?: boolean;
      /** Set only when the offline outbox replays a queued start: the stamp as it was taken. */
      replayStamp?: JobStamp;
    }) =>
      runWithStamp<Record<string, unknown>>({
        kind: "start",
        taskId,
        propertyId,
        extra: { extraDirty, extraDirtyNotes, earlyCleaningStarted },
        replay: replayStamp ? { stamp: replayStamp } : undefined,
        queuedResult: (stamp) => ({ id: taskId, property_id: propertyId, status: "in_progress", started_at: stamp.at }),
        run: async (stamp, stampSettings) => {
      console.log("[useStartCleaningTimer] Starting task:", taskId);

      // Guard: never "start" a task that is already completed (prevents restart loop after success page)
      const { data: existing, error: fetchErr } = await supabase
        .from("cleaning_tasks")
        .select("id, status, scheduled_date, checklist_completed, inventory_completed, started_at, cleaner_id, assignee_user_id, assigned_type, extra_dirty, extra_dirty_notes")
        .eq("id", taskId)
        .maybeSingle();
      if (!fetchErr && existing) {
        const alreadyCompleted =
          (existing as { status?: string }).status === "completed" ||
          ((existing as { checklist_completed?: boolean }).checklist_completed &&
            (existing as { inventory_completed?: boolean }).inventory_completed);
        if (alreadyCompleted) {
          console.log("[useStartCleaningTimer] Task already completed, skipping start (prevents restart loop)");
          return existing as Record<string, unknown>;
        }
      }
      // If fetch failed (e.g. 404), do not start — task may be completed or removed
      if (fetchErr) {
        console.warn("[useStartCleaningTimer] Could not load task, skipping start:", fetchErr.message);
        throw fetchErr;
      }

      if (cleaner?.id) {
        const { data: assignRows, error: assignErr } = await supabase
          .from("cleaning_task_checklist_assignments")
          .select("cleaner_id, assigned_type, assignee_user_id")
          .eq("cleaning_task_id", taskId);
        if (assignErr) throw assignErr;
        const rows = assignRows ?? [];
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        const authUid = authUser?.id ?? null;
        const taskAssignedToUser = Boolean(
          authUid &&
            (existing as { assignee_user_id?: string | null; assigned_type?: string | null }).assignee_user_id ===
              authUid &&
            ["team", "owner", "user"].includes(
              String((existing as { assigned_type?: string | null }).assigned_type ?? "")
            )
        );
        const assignedToThisUser = Boolean(
          authUid &&
            rows.some(
              (r) =>
                r.assignee_user_id === authUid &&
                (r.assigned_type === "team" || r.assigned_type === "owner" || r.assigned_type === "user")
            )
        );
        // Team / owner runners are not cleaner-id assignees — skip cleaner binding check.
        if (!assignedToThisUser && !taskAssignedToUser) {
          const junctionCleanerIds = [
            ...new Set(
              rows
                .filter((r) => r.assigned_type === "cleaner" && r.cleaner_id)
                .map((r) => r.cleaner_id as string)
            ),
          ];
          const taskCleanerId = (existing as { cleaner_id?: string | null }).cleaner_id ?? null;
          if (junctionCleanerIds.length > 0) {
            if (!junctionCleanerIds.includes(cleaner.id)) {
              throw new Error("CLEANING_START_NOT_ASSIGNED_CLEANER");
            }
          } else if (rows.length > 0) {
            throw new Error("CLEANING_START_NOT_ASSIGNED_CLEANER");
          } else if (taskCleanerId && taskCleanerId !== cleaner.id) {
            throw new Error("CLEANING_START_NOT_ASSIGNED_CLEANER");
          }
        }
      }

      const ex = existing as {
        status?: string;
        scheduled_date?: string;
        started_at?: string | null;
      } | null;
      const alreadyInProgress = ex?.status === "in_progress" && Boolean(ex?.started_at);

      if (
        enforceScheduledDay &&
        !alreadyInProgress &&
        ex?.status === "scheduled" &&
        ex.scheduled_date &&
        !canStartTurnoverClean(ex.scheduled_date)
      ) {
        throw new Error("CLEANING_START_NOT_SCHEDULED_DAY");
      }

      const updatePayload: Record<string, unknown> = alreadyInProgress
        ? {
            // Visit already clocked in (e.g. first tap on Start task); merge extras + GPS only.
            extra_dirty: extraDirty ?? false,
            extra_dirty_notes: extraDirtyNotes ?? null,
          }
        : {
            started_at: stamp.at,
            status: "in_progress",
            // A Test clean can be set up as extra dirty before the cleaner screens start.
            extra_dirty: extraDirty || (existing as { extra_dirty?: boolean | null } | null)?.extra_dirty || false,
            extra_dirty_notes:
              extraDirtyNotes || (existing as { extra_dirty_notes?: string | null } | null)?.extra_dirty_notes || null,
            early_cleaning_started: earlyCleaningStarted || false,
          };

      if (!alreadyInProgress) {
        // First start only — preserve early flag on subsequent merges when omitted
        (updatePayload as { early_cleaning_started?: boolean }).early_cleaning_started =
          earlyCleaningStarted || false;
      } else if (earlyCleaningStarted === true) {
        (updatePayload as { early_cleaning_started?: boolean }).early_cleaning_started = true;
      }

      // Bind task to this cleaner so submit-inventory and completion lookups always find it
      if (cleaner?.id) {
        updatePayload.cleaner_id = cleaner.id;
      }

      if (!alreadyInProgress && !startLocation) {
        Object.assign(updatePayload, stampColumns("start", stamp, stampSettings));
      }

      // Slice 50: the arrival checklist as it stands now, so later edits only apply to the next job.
      if (!alreadyInProgress) {
        const arrivalTasks = await fetchArrivalTasksForJob(propertyId);
        if (arrivalTasks !== undefined) updatePayload.arrival_tasks = arrivalTasks;
      }

      if (startLocation) {
        updatePayload.start_location_lat = startLocation.lat ?? null;
        updatePayload.start_location_lng = startLocation.lng ?? null;
        updatePayload.start_location_accuracy_m = startLocation.accuracyM ?? null;
        updatePayload.start_location_distance_m = startLocation.distanceM ?? null;
        updatePayload.start_location_verified = startLocation.verified;
        updatePayload.start_location_override_reason = startLocation.overrideReason;
        updatePayload.start_location_captured_at = startLocation.capturedAt;
      }

      const { data, error } = await supabase
        .from("cleaning_tasks")
        .update(updatePayload)
        .eq("id", taskId)
        .select()
        .single();

      if (error) {
        console.error("[useStartCleaningTimer] Error:", error);
        throw error;
      }

      // On-site start ⇒ guest has left (covers guests who never message checkout).
      if (!alreadyInProgress) {
        const { error: leftErr } = await supabase.rpc("mark_guest_left_on_cleaning_start", {
          p_task_id: taskId,
        });
        if (leftErr) {
          console.warn("[useStartCleaningTimer] mark_guest_left_on_cleaning_start:", leftErr.message);
        }
      }

      console.log("[useStartCleaningTimer] Task started successfully:", data);
      return data as Record<string, unknown>;
        },
      }),
    onSuccess: async (data, variables) => {
      const taskPropertyId =
        (data as { property_id?: string } | null)?.property_id ?? variables.propertyId;
      console.log("[useStartCleaningTimer] Invalidating caches for property:", taskPropertyId);

      // Invalidate all relevant cleaner task queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", taskPropertyId] }),
        queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner"] }),
        queryClient.invalidateQueries({ queryKey: ["my-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["refresh-turnover-window"] }),
      ]);
      
      console.log("[useStartCleaningTimer] Cache invalidation complete");
    },
  });
};

export const useUpdateUnitEmptyOnArrival = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      taskId, 
      unitEmptyOnArrival,
    }: { 
      taskId: string; 
      propertyId: string;
      unitEmptyOnArrival: boolean;
    }) => {
      const { data, error } = await supabase
        .from("cleaning_tasks")
        .update({ 
          unit_empty_on_arrival: unitEmptyOnArrival,
        })
        .eq("id", taskId)
        .select()
        .single();

      if (error) throw error;

      // Explicit "guest left early" answer also flips Guest left on the job clocks.
      if (unitEmptyOnArrival) {
        const { error: leftErr } = await supabase.rpc("mark_guest_left_on_cleaning_start", {
          p_task_id: taskId,
        });
        if (leftErr) {
          console.warn("[useUpdateUnitEmptyOnArrival] mark_guest_left_on_cleaning_start:", leftErr.message);
        }
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["refresh-turnover-window"] });
    },
  });
};
